import moment from "moment";
import { getProtoTimestampAsDateStringSecond } from "../../shared/utils";
import {
  Bundle,
  BundleTranslation,
  Multimedia,
} from "../../../gen/witbooking/backoffice/bundle/v1/bundle_api_pb";
import {
  MultimediaTranslation,
  TravelWindow,
} from "../../../gen/witbooking/backoffice/common/common_pb";
import {
  CustomBundle,
  RangeDateWindowsType,
  WEEK_DAYS,
  BundlePriceType,
  RangeDateWindowsGRPCType,
  TranslationType,
  MultimediaTranslationType,
  OtherValuesType,
} from "./types";
import { FormType } from "../EditBundle/types";
import { compareAsc } from "date-fns";
import { stringToDate } from "../../../lib/dateUtils";

export const mapToObjectRangeWindows = (
  windowsList: RangeDateWindowsGRPCType[]
): RangeDateWindowsType[] => {
  return windowsList.map((item, index) => ({
    id: index.toString(),
    startDate: getProtoTimestampAsDateStringSecond(item.startDate, "yyyy-MM-dd"),
    endDate: getProtoTimestampAsDateStringSecond(item.endDate, "yyyy-MM-dd"),
    weekDays: item.weekDaysList.map((item: number) => WEEK_DAYS[item]),
  }))
    .sort((a, b) => compareAsc(stringToDate(a.startDate), stringToDate(b.startDate)));
};

const mapToObjectTranslationsList = (
  translationsList: TranslationType[],
  allowedLocales: string[]
) => {
  return allowedLocales.map((locale: string) => {
    const element = translationsList.find((item: TranslationType) => item.locale === locale);
    return element
      ? { locale, name: element?.name, description: element?.description }
      : { locale, name: "", description: "" };
  });
};

const mapToObjectBundleTypePrice = (value: number) => {
  return BundlePriceType[value];
};

const mapToGRPCRangeWindow = (item: Omit<RangeDateWindowsType, "id">) => {
  const result = new TravelWindow();
  // @ts-ignore
  const startDate = new proto.google.protobuf.Timestamp();
  startDate.setNanos(0);
  const startDateEpoch = moment(item.startDate).unix();
  startDate.setSeconds(startDateEpoch);
  result.setStartDate(startDate);

  // @ts-ignore
  const endDate = new proto.google.protobuf.Timestamp();
  endDate.setNanos(0);
  const endDateEpoch = moment(item.endDate).unix();
  endDate.setSeconds(endDateEpoch);
  result.setEndDate(endDate);

  // @ts-ignore
  result.setWeekDaysList(item.weekDays.map((day: string) => WEEK_DAYS[day]));

  return result;
};

const mapToGRPCTranslationsList = (value: BundleTranslation.AsObject) => {
  const result = new BundleTranslation();
  result.setLocale(value.locale);
  result.setName(value.name);
  result.setDescription(value.description);

  return result;
};

const mapToGRPCMultimediaTranslationsList = (
  translationList: MultimediaTranslationType
): MultimediaTranslation[] => {
  return Object.keys(translationList).map((keyLocale: string) => {
    const item = translationList[keyLocale];
    const translation = new MultimediaTranslation();
    translation.setLocale(keyLocale);
    translation.setTitle(item.title);
    translation.setDescription(item.description);
    return translation;
  });
};

export const transformMultimediaToImageManageObject = (
  translationList: MultimediaTranslationType
): MultimediaTranslation.AsObject[] => {
  return Object.keys(translationList).map((keyLocale: string) => {
    const item = translationList[keyLocale];

    return {
      locale: keyLocale,
      description: item.description,
      title: item.title,
    } as MultimediaTranslation.AsObject;
  });
};

const mapToGRPCMultimediaList = (value: any): Multimedia => {
  const result = new Multimedia();

  result.setId(value.id);
  result.setFilename(value.filename);
  result.setUrl(value.url);
  result.setTranslationsList(mapToGRPCMultimediaTranslationsList(value.translations));

  return result;
};

function multimediaFromImage(multimediaList: Multimedia.AsObject[]): any {
  return multimediaList.map(multimedia =>
    Object.assign(new File([""], multimedia.filename), {
      id: multimedia.id,
      url: multimedia.url,
      filename: multimedia.filename,
      translations:
        (multimedia.translationsList &&
          multimedia.translationsList.length &&
          multimedia.translationsList.reduce((accum, curr) => {
            const locale = curr.locale;
            return {
              ...accum,
              [locale]: {
                title: curr.title,
                description: curr.description,
              },
            };
          }, {})) ||
        {},
    })
  );
}

export const mapToObjectFromGRPC = (
  bundleGRPC: Bundle.AsObject,
  allowedLocales: string[]
): CustomBundle =>
  (({
    ticker: bundleGRPC.ticker,
    active: bundleGRPC.active,
    inventoryLinesList: bundleGRPC.inventoryLinesList,
    extrasList: bundleGRPC.extrasList,
    promotionsList: bundleGRPC.promotionsList,
    codesList: bundleGRPC.codesList,
    travelWindowsList: mapToObjectRangeWindows(
      bundleGRPC.travelWindowsList as RangeDateWindowsGRPCType[]
    ),
    translationsList: mapToObjectTranslationsList(bundleGRPC.translationsList, allowedLocales),
    bundlePriceType: mapToObjectBundleTypePrice(bundleGRPC.bundlePriceType),
    bundlePrice: bundleGRPC.bundlePrice,
    id: bundleGRPC.id,
    multimediaList: multimediaFromImage(bundleGRPC.multimediaList),
  } as unknown) as CustomBundle);

export const mapToGRPCFromObject = (data: FormType, otherValues: OtherValuesType): Bundle => {
  const bundleObj = new Bundle();
  const bundlePriceType = BundlePriceType[data.bundlePriceType as keyof typeof BundlePriceType];
  const codeList = data.codesList
    ?.toString()
    .split(";")
    .filter(i => i !== "");
  bundleObj.setId(otherValues.id);
  bundleObj.setActive(data.active);
  bundleObj.setTicker(data.ticker);

  if (codeList.length > 0) {
    bundleObj.setCodesList(codeList);
  }

  bundleObj.setBundlePriceType(bundlePriceType);
  bundleObj.setExtrasList(data.extrasList);
  bundleObj.setPromotionsList(data.promotionsList);

  bundleObj.setInventoryLinesList(data.inventoryLinesList);
  bundleObj.setTranslationsList(data.translationsList.map(item => mapToGRPCTranslationsList(item)));

  bundleObj.setTravelWindowsList(
    otherValues.travelWindows.map((item: RangeDateWindowsType) => mapToGRPCRangeWindow(item))
  );
  if (otherValues.multimediaList.length > 0) {
    bundleObj.setMultimediaList(
      otherValues.multimediaList
        .filter(m => !m.url.includes("blob:"))
        .map((item: any) => mapToGRPCMultimediaList(item))
    );
  }
  bundleObj.setBundlePrice(data.bundlePrice);

  //Check these values: defaults
  bundleObj.setBundleRestrictionType(0);
  bundleObj.setVisible(true);
  bundleObj.setMaxRelease(0);
  bundleObj.setMaxStay(0);
  bundleObj.setMinRelease(0);
  bundleObj.setMinStay(0);
  return bundleObj;
};
